import * as Sentry from '@sentry/react';
import { PostgrestError } from '@supabase/postgrest-js';

const env = import.meta.env ?? {};

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  timestamp: string;
}

/**
 * Detect whether an unknown value is shaped like a Supabase PostgrestError.
 * We use duck-typing instead of `instanceof` because bundlers may duplicate
 * the PostgrestError class across chunks.
 */
function isPostgrestError(error: unknown): error is PostgrestError {
  return (
    error instanceof PostgrestError ||
    (error instanceof Error &&
      error.name === 'PostgrestError' &&
      'code' in error &&
      'details' in error)
  );
}

class Logger {
  private isDevelopment = Boolean(env.DEV);
  
  private log(level: LogLevel, message: string, context?: Record<string, unknown>) {
    const logEntry: LogEntry = {
      level,
      message,
      context,
      timestamp: new Date().toISOString()
    };

    if (this.isDevelopment) {
      const consoleMethod = level === 'debug' ? 'log' : level;
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`, context || '');
    }

    // #10 — Forward to Sentry structured logs in production
    if (env.PROD && Sentry.logger) {
      const attrs = context
        ? Object.fromEntries(
            Object.entries(context)
              .filter(([, v]) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
          )
        : undefined;

      switch (level) {
        case 'debug':
          Sentry.logger.debug(message, attrs);
          break;
        case 'info':
          Sentry.logger.info(message, attrs);
          break;
        case 'warn':
          Sentry.logger.warn(message, attrs);
          break;
        case 'error':
          Sentry.logger.error(message, attrs);
          break;
      }
    }
  }

  debug(message: string, context?: Record<string, unknown>) {
    this.log('debug', message, context);
  }

  info(message: string, context?: Record<string, unknown>) {
    this.log('info', message, context);
  }

  warn(message: string, context?: Record<string, unknown>) {
    this.log('warn', message, context);
  }

  error(message: string, error?: unknown, context?: Record<string, unknown>) {
    const errorContext: Record<string, unknown> = {
      ...context,
      ...(isPostgrestError(error) && {
        errorMessage: error.message,
        errorCode: error.code,
        errorDetails: error.details,
        errorHint: error.hint,
        errorStack: error.stack,
      }),
      ...(error instanceof Error && !isPostgrestError(error) && {
        errorMessage: error.message,
        errorStack: error.stack,
      }),
    };

    // Production: forward errors to Sentry
    if (env.PROD) {
      if (error instanceof Error) {
        Sentry.captureException(error, { extra: errorContext });
      } else {
        Sentry.captureMessage(message, { level: 'error', extra: errorContext });
      }
    }

    this.log('error', message, errorContext);
  }
}

export const logger = new Logger();

/** Extract a user-facing message string from an unknown catch value.
 *  Supabase PostgrestError `.details` are included when available. */
export function getErrorMessage(error: unknown): string {
  if (isPostgrestError(error)) {
    return error.details
      ? `${error.message} (${error.details})`
      : error.message;
  }
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'An unknown error occurred';
}
